import { useEffect, useRef } from 'react';
import { useApi } from './useApi';
import {
  getRefreshToken,
  getSessionConfig,
  getToken,
  isIdleExceeded,
  recordActivity,
  redirectToSessionExpired,
  setSession,
} from '../utils/session';

const ACTIVITY_THROTTLE_MS = 10 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];

// Silently renews the access token shortly before its own natural expiry —
// accessTokenExpiryMinutes minus renewBufferSeconds, both DB-configurable
// (irm_session_config), e.g. a 15-minute token with a 30s buffer renews at
// the 14:30 mark — via a single scheduled setTimeout per token lifetime,
// rescheduled after every successful renewal, instead of polling on a
// fixed interval regardless of how close the token actually is to expiry.
//
// Scheduling itself is independent of real user activity (it exists to
// keep the access token from going stale during a long continuously-active
// session) — but every successful renewal, scheduled or manual, also
// counts as activity and resets the visible idle countdown (SessionIdleTimer),
// so a tab that stays open keeps renewing itself instead of drifting into
// the warning/critical stages. Idle-based forced logout is still enforced
// independently by SessionIdleTimer (ticks every second); the
// isIdleExceeded check below is only a redundant safety net in case that
// component somehow isn't mounted.
//
// Mounted once in App.js; a no-op whenever there's no token to watch.
export function useSessionWatcher() {
  const { user } = useApi();
  const lastActivityWriteRef = useRef(0);
  const tickingRef = useRef(false);
  const timeoutIdRef = useRef(null);
  const scheduledAtRef = useRef(0);

  useEffect(() => {
    if (!getToken()) return undefined;

    recordActivity();

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityWriteRef.current = now;
      recordActivity();
    };

    const scheduleFrom = (config) => {
      const { accessTokenExpiryMinutes, renewBufferSeconds } = config;
      const delayMs = Math.max(0, accessTokenExpiryMinutes * 60 * 1000 - renewBufferSeconds * 1000);
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      scheduledAtRef.current = Date.now() + delayMs;
      // eslint-disable-next-line no-use-before-define
      timeoutIdRef.current = setTimeout(renewOrExpire, delayMs);
    };

    const renewOrExpire = async () => {
      if (!getToken() || tickingRef.current) return;
      tickingRef.current = true;
      let attemptedToken = null;

      try {
        if (isIdleExceeded(getSessionConfig().idleTimeoutMinutes)) {
          redirectToSessionExpired('Session expired due to inactivity.');
          return;
        }

        attemptedToken = getRefreshToken();
        if (!attemptedToken) return;

        const data = await user.refreshToken(attemptedToken);
        setSession({
          token: data.data.token,
          refresh_token: data.data.refresh_token,
          session_config: data.data.session_config,
        });
        recordActivity();
        scheduleFrom(data.data.session_config || getSessionConfig());
      } catch {
        // Refresh tokens rotate on every use — if another tab already
        // refreshed (and thus rotated) the token in the meantime, the token
        // we just tried is expectedly stale, not actually expired (the
        // stored refresh_token will already have moved on). Only force
        // logout if it's still the same token we attempted with; otherwise
        // resync our schedule to whatever the other tab just stored.
        if (getRefreshToken() === attemptedToken) {
          redirectToSessionExpired('Session expired due to inactivity.');
        } else {
          scheduleFrom(getSessionConfig());
        }
      } finally {
        tickingRef.current = false;
      }
    };

    const onVisibilityChange = () => {
      // Background tabs can have their timers throttled/paused by the
      // browser — if we come back after the scheduled moment has already
      // passed, catch up immediately instead of waiting for a timeout that
      // may never fire.
      if (document.visibilityState === 'visible' && Date.now() >= scheduledAtRef.current) {
        renewOrExpire();
      }
    };

    ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibilityChange);
    scheduleFrom(getSessionConfig());

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, onActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
